import { type FC } from 'react';

import Pagination from 'molecules/Pagination';
import Section from 'molecules/Section';
import { splitSectionProps } from 'molecules/Section/config';

import ScrollFadeSection from 'components/Animations/ScrollFadeSection';

import StandardFeed from 'layout/Listing/components/StandardFeed';
import { ListingConfigProps } from 'layout/Listing/config';
import usePagination from 'layout/Listing/hooks/usePagination';
import useSaveReturnUrl from 'layout/Listing/hooks/useSaveReturnUrl';
import BlogFilters from 'layout/Listing/variants/blog/components/Filters';
import SearchBar from 'layout/Listing/variants/blog/components/SearchBar';
import useFilterSearchParam from 'layout/Listing/variants/blog/hooks/useFilterBlogSearchParam';
import useFilterBlogs from 'layout/Listing/variants/blog/hooks/useFilteredBlogs';

import { hasArrayValues } from 'utils/array/arrays';
import { mergeListings } from 'utils/array/mergeListings';
import { toArray } from 'utils/array/toArray';
import { getBuilderStateData } from 'utils/builder';
import useForceReloadOnBackToResources from 'utils/hooks/useForceReloadOnBackToResources';
import { parseListingDate } from 'utils/parse/parseDate';

import blogs from 'data/blogs.json';

const PAGE_SIZE = 12;

type BlogItem = { data: BlogResource };

const BlogListing: FC<ListingConfigProps> = props => {
  const {
    sectionProps,
    rest: { newsletterCard, builderState, searchPlaceholder, headingTitle },
  } = splitSectionProps(props);

  const rawLatestData = getBuilderStateData('listingData', builderState);
  const latestData = toArray<BlogItem>(rawLatestData);
  const localData = toArray<BlogItem>(blogs);

  const blogList = hasArrayValues(latestData) ? mergeListings(latestData, localData) : localData;

  if (!hasArrayValues(blogList)) {
    console.warn('[BlogListing] No items to render – check builderState/listingData and blogs.json');
  }

  useFilterSearchParam();

  const filteredBlogs = useFilterBlogs(blogList).sort((a, b) => {
    const dateA = parseListingDate(a.data?.lastUpdated) || parseListingDate(a.data?.publishDate);
    const dateB = parseListingDate(b.data?.lastUpdated) || parseListingDate(b.data?.publishDate);

    return dateB - dateA;
  });

  const { currentPage, paginatedItems, totalItems, handlePageChange } = usePagination({
    items: filteredBlogs,
    pageSize: PAGE_SIZE,
  });

  useSaveReturnUrl('/blog');
  useForceReloadOnBackToResources();

  return (
    <>
      <Section {...sectionProps} darkMode={false}>
        <ScrollFadeSection delay={0.1} containPaint={false}>
          <div id="listing-section" className="flex flex-col gap-8">
            <div className="grid grid-cols-1 justify-center gap-8 sm:grid-cols-3 lg:grid-cols-2">
              <h2 className="font-spaceGrotesk text-display-md font-semibold text-gray-900 sm:max-lg:col-span-full">
                {headingTitle || 'All Posts'}
              </h2>

              <BlogFilters />
              <SearchBar placeholder={searchPlaceholder} />
            </div>

            <StandardFeed posts={paginatedItems} newsletterCard={newsletterCard} />

            {totalItems > PAGE_SIZE && (
              <Pagination
                count={totalItems}
                pageSize={PAGE_SIZE}
                setPage={detail => handlePageChange(detail.page)}
                currentPage={currentPage}
              />
            )}
          </div>
        </ScrollFadeSection>
      </Section>
      <nav aria-label="All blog posts" className="sr-only">
        {filteredBlogs
          .filter(post => post.data?.url)
          .map(post => (
            <a key={`seo-${post.data.url}`} href={post.data.url?.endsWith('/') ? post.data.url : `${post.data.url}/`}>
              {post.data.title}
            </a>
          ))}
      </nav>
    </>
  );
};

export default BlogListing;
